import React, { createContext, useContext, useEffect, useState } from 'react';
import { getNotices } from '../api';
import { useAuth } from './AuthContext';
import { parseApiDateTime } from '../utils/apiDateTime';

interface NoticeContextType {
  unreadCount: number;
  newCount: number;
  refreshUnread: () => Promise<void>;
  decrementUnread: () => void;
}

const NoticeContext = createContext<NoticeContextType | undefined>(undefined);

export const NoticeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [newCount, setNewCount] = useState(0);

  const refreshUnread = async () => {
    try {
      const notices = await getNotices();
      if (isAuthenticated) {
        setUnreadCount(notices.filter((n) => !n.is_read).length);
        setNewCount(0);
      } else {
        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
        setNewCount(
          notices.filter((n) => {
            const publishedAt = parseApiDateTime(n.published_at);
            return publishedAt ? publishedAt >= twoWeeksAgo : false;
          }).length,
        );
        setUnreadCount(0);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    refreshUnread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const decrementUnread = () => {
    setUnreadCount((prev) => (prev > 0 ? prev - 1 : 0));
  };

  return (
    <NoticeContext.Provider
      value={{ unreadCount, newCount, refreshUnread, decrementUnread }}
    >
      {children}
    </NoticeContext.Provider>
  );
};

export const useNotice = (): NoticeContextType => {
  const context = useContext(NoticeContext);
  if (!context) {
    throw new Error('useNotice must be used within a NoticeProvider');
  }
  return context;
};
